import { Link, useNavigate } from '@tanstack/react-router'
import { Package, Search } from 'lucide-react'
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { ChoicePicker, type PickerChoice } from '@/components/choice-picker'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { SearchInput } from '@/components/search-input'
import { SearchSelect } from '@/components/search-select'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { usePageTitle } from '@/lib/page-title'
import { parseUTCDate, resultCount } from '@/lib/utils'
import {
  WARRANTY_CLAIM_FILTERS,
  WARRANTY_CLAIM_STATUS,
  useUpdateWarrantyClaim,
  useWarrantyClaims,
  type WarrantyClaim,
  type WarrantyClaimFilter,
  type WarrantyClaimStatus,
} from './api'
import { WarrantyActionItem } from './claim-actions'

const FILTER_LABELS: Record<WarrantyClaimFilter, string> = {
  open: 'Open',
  ...Object.fromEntries(
    Object.entries(WARRANTY_CLAIM_STATUS).map(([value, status]) => [value, status.label]),
  ) as Record<WarrantyClaimStatus, string>,
  all: 'All claims',
}

const FILTER_OPTIONS = WARRANTY_CLAIM_FILTERS.filter((value) => value !== 'all').map((value) => ({
  value,
  label: FILTER_LABELS[value],
}))

const CHOICES: PickerChoice[] = (Object.keys(WARRANTY_CLAIM_STATUS) as WarrantyClaimStatus[]).map((value) => ({
  value,
  label: WARRANTY_CLAIM_STATUS[value].label,
  tone: WARRANTY_CLAIM_STATUS[value].tone,
}))

const filed = (claim: WarrantyClaim) =>
  parseUTCDate(claim.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

/** Rows open on the item's purchase order, where the claim is worked; its project when it has none. */
function useOpenClaim() {
  const navigate = useNavigate()
  return (claim: WarrantyClaim) =>
    void (claim.po_uuid
      ? navigate({ to: '/purchase-orders/$poId', params: { poId: claim.po_uuid }, hash: 'warranty-claims' })
      : navigate({ to: '/projects/$projectId', params: { projectId: claim.project_uuid } }))
}

function Item({ claim }: { claim: WarrantyClaim }) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      {claim.image_url ? (
        <img
          alt=""
          src={claim.image_url}
          loading="lazy"
          className="size-10 shrink-0 rounded-md border bg-white object-contain p-0.5"
        />
      ) : (
        <span className="grid size-10 shrink-0 place-items-center rounded-md bg-muted">
          <Package aria-hidden="true" className="size-4 text-muted-foreground" />
        </span>
      )}
      <div className="min-w-0">
        <span className="line-clamp-2 font-medium">{claim.item_name}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {[claim.number, claim.manufacturer_name].filter(Boolean).join(' · ')}
        </span>
      </div>
    </div>
  )
}

/** The customer, linked to their page, and the project the item was bought on. */
function Customer({ claim }: { claim: WarrantyClaim }) {
  return (
    <div className="min-w-0">
      {claim.customer_group_uuid ? (
        <Link
          className="block truncate font-medium hover:underline"
          onClick={(event) => event.stopPropagation()}
          params={{ groupId: claim.customer_group_uuid }}
          to="/customers/$groupId"
        >
          {claim.customer_name}
        </Link>
      ) : (
        <span className="block truncate font-medium">{claim.contact_name}</span>
      )}
      <Link
        className="block truncate text-xs text-muted-foreground hover:underline"
        onClick={(event) => event.stopPropagation()}
        params={{ projectId: claim.project_uuid }}
        to="/projects/$projectId"
      >
        {claim.project_name}
      </Link>
    </div>
  )
}

function Problem({ claim }: { claim: WarrantyClaim }) {
  return (
    <div className="min-w-0 space-y-1">
      {claim.unit_down ? <StatusPill tone="danger">Not working</StatusPill> : null}
      <p className="line-clamp-2 text-sm">{claim.problem}</p>
    </div>
  )
}

function Status({ claim }: { claim: WarrantyClaim }) {
  const update = useUpdateWarrantyClaim()
  const status = WARRANTY_CLAIM_STATUS[claim.status]
  return (
    <div className="space-y-1" onClick={(event) => event.stopPropagation()}>
      <ChoicePicker
        title="Claim status"
        label={`Claim ${claim.number} status: ${status.label}. Change status`}
        value={claim.status}
        choices={CHOICES}
        onSelect={(value) => update.mutate({ uuid: claim.uuid, update: { status: value as WarrantyClaimStatus } })}
        pending={update.isPending}
      >
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
      </ChoicePicker>
      <p className="text-xs tabular-nums text-muted-foreground">Filed {filed(claim)}</p>
    </div>
  )
}

function ListSkeleton() {
  return (
    <div aria-label="Loading" className="space-y-2" role="status">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton className="h-14 w-full" key={index} />
      ))}
    </div>
  )
}

/** Every warranty claim customers filed from their order trackers (USA-915), open ones first by default. */
export function WarrantyClaimsPage() {
  usePageTitle('Warranty claims')
  const openClaim = useOpenClaim()
  const [search, setSearch] = useUrlSearchText()
  const [{ status, page, action }, setFilters] = useQueryStates({
    status: parseAsStringLiteral(WARRANTY_CLAIM_FILTERS).withDefault('open'),
    page: parseAsInteger.withDefault(1),
    // A warranty@ email no claim holds yet, opened from the Action Items page.
    action: parseAsString,
  })
  const list = useWarrantyClaims(status, search.trim(), page)
  const rows = list.data?.items ?? []
  const pages = Math.max(list.data?.pages ?? 1, 1)

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      <h1 className="text-2xl font-bold tracking-tight">Warranty claims</h1>

      {action ? <WarrantyActionItem actionUuid={action} /> : null}

      <ListFilters
        count={resultCount(list.data?.total, 'claim')}
        active={status === 'open' ? 0 : 1}
        onClear={() => void setFilters({ status: null, page: 1 })}
        search={
          <label className="relative block">
            <span className="sr-only">Search warranty claims</span>
            <Search aria-hidden="true" className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              className="pl-9"
              onChange={(event) => {
                setSearch(event.target.value)
                void setFilters({ page: 1 })
              }}
              placeholder="Item, customer, serial or claim number"
              value={search}
            />
          </label>
        }
      >
        <ListFilter label="Show">
          <SearchSelect
            allLabel={FILTER_LABELS.all}
            ariaLabel="Status filter"
            className="h-9 w-full"
            onChange={(next) => void setFilters({ status: (next as WarrantyClaimFilter | undefined) ?? 'all', page: 1 })}
            options={FILTER_OPTIONS}
            searchPlaceholder="Search statuses"
            title="Show"
            value={status === 'all' ? undefined : status}
          />
        </ListFilter>
      </ListFilters>

      {list.isLoading ? (
        <ListSkeleton />
      ) : !rows.length ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          {status === 'open' && !search.trim() ? 'No open claims' : 'No claims'}
        </p>
      ) : (
        <>
          <div className="hidden lg:block">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="w-[22%]">Customer</TableHead>
                  <TableHead className="w-[32%]">Problem</TableHead>
                  <TableHead className="w-[170px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((claim) => (
                  <TableRow className="cursor-pointer align-top" key={claim.uuid} onClick={() => openClaim(claim)}>
                    <TableCell className="whitespace-normal">
                      <Item claim={claim} />
                    </TableCell>
                    <TableCell>
                      <Customer claim={claim} />
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      <Problem claim={claim} />
                    </TableCell>
                    <TableCell>
                      <Status claim={claim} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <ul className="grid gap-2 lg:hidden">
            {rows.map((claim) => (
              <li
                className="cursor-pointer space-y-2 rounded-lg border bg-card p-3 hover:bg-muted/30"
                key={claim.uuid}
                onClick={() => openClaim(claim)}
              >
                <Item claim={claim} />
                <div className="flex items-start justify-between gap-3">
                  <Customer claim={claim} />
                  <div className="shrink-0 text-right">
                    <Status claim={claim} />
                  </div>
                </div>
                <Problem claim={claim} />
              </li>
            ))}
          </ul>
        </>
      )}

      {pages > 1 ? (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {list.data?.page ?? page} of {pages}
          </p>
          <div className="flex gap-2">
            <Button disabled={page <= 1} onClick={() => void setFilters({ page: page - 1 })} size="sm" variant="outline">
              Previous
            </Button>
            <Button disabled={page >= pages} onClick={() => void setFilters({ page: page + 1 })} size="sm" variant="outline">
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
